import { Device } from '@twilio/voice-sdk';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { authenticatedFetch } from '../../api/apiClient';
import { emitAppNotification } from '../appNotifications';
import notificationStore from '../../stores/notificationStore';
import { loadPhoneAudioSettings } from '../../utils/browserAudioDevices';

/** Explicit call lifecycle states for the single active slot. */
export const VOICE_CALL_STATE = Object.freeze({
  IDLE: 'idle',
  INCOMING: 'incoming',
  ACCEPTING: 'accepting',
  ACTIVE: 'active',
  OUTBOUND_RINGING: 'outbound-ringing',
  CONNECTED: 'connected',
  TERMINATING: 'terminating',
  ENDED: 'ended',
});

const BUSY_STATES = new Set([
  VOICE_CALL_STATE.INCOMING,
  VOICE_CALL_STATE.ACCEPTING,
  VOICE_CALL_STATE.ACTIVE,
  VOICE_CALL_STATE.OUTBOUND_RINGING,
  VOICE_CALL_STATE.CONNECTED,
  VOICE_CALL_STATE.TERMINATING,
]);

let voiceDeviceInstance = null;
let voiceDeviceUid = null;
let voiceDeviceInitPromise = null;
/** @type {null | {
 *   call: object,
 *   internalId: string,
 *   callSid: string,
 *   state: string,
 *   detail: object | null,
 *   notificationId: string | null,
 *   outboundParams: object | null,
 * }} */
let activeCallRecord = null;
/** Operator-facing end reasons (brief; no Twilio jargon in UI consumers). */
export const VOICE_END_REASON = Object.freeze({
  REJECTED: 'rejected',
  ENDED: 'ended',
  MISSED: 'missed',
  CONNECTION_FAILED: 'connection_failed',
  BUSY: 'busy',
});

let tokenRefreshPromise = null;
let outboundInFlight = false;
let isMuted = false;
let endpointSessionId = null;
let internalCallSeq = 0;
/** When true, call register() once we return to idle (token was updated while busy). */
let deferredRegisterAfterIdle = false;
let voiceDeviceReady = false;

const edge = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TWILIO_EDGE) || 'ashburn';

const remoteAudio = typeof Audio !== 'undefined' ? new Audio() : null;
if (remoteAudio) {
  remoteAudio.autoplay = true;
}

function ensureEndpointSessionId() {
  if (endpointSessionId) return endpointSessionId;
  try {
    endpointSessionId = (
      globalThis.crypto?.randomUUID?.()
      || `ep-${Date.now()}-${Math.random().toString(16).slice(2)}`
    );
  } catch (_) {
    endpointSessionId = `ep-${Date.now()}`;
  }
  return endpointSessionId;
}

function nextInternalCallId() {
  internalCallSeq += 1;
  return `local-${Date.now()}-${internalCallSeq}`;
}

function currentAudioSettings() {
  return loadPhoneAudioSettings();
}

function voiceLog(event, fields = {}) {
  try {
    console.log('[VOICE]', {
      event,
      endpointSessionId: ensureEndpointSessionId(),
      uid: voiceDeviceUid || null,
      identity: voiceDeviceUid ? `user-${voiceDeviceUid}` : null,
      callSid: activeCallRecord?.callSid || null,
      internalCallId: activeCallRecord?.internalId || null,
      state: activeCallRecord?.state || VOICE_CALL_STATE.IDLE,
      ...fields,
    });
  } catch (_) {
    /* ignore logging failures */
  }
}

async function applySpeakerToElement(el, speakerDeviceId) {
  if (!el?.setSinkId || !speakerDeviceId) return;
  try {
    await el.setSinkId(speakerDeviceId);
  } catch (error) {
    console.warn('[VOICE] setSinkId failed', error);
  }
}

function attachRemoteStream(call) {
  if (!remoteAudio || !call?.getRemoteStream) return;
  const stream = call.getRemoteStream();
  if (!stream) return;
  remoteAudio.srcObject = stream;
  applySpeakerToElement(remoteAudio, currentAudioSettings().speakerDeviceId);
  remoteAudio.play?.().catch((error) => {
    console.warn('[VOICE] remote audio play failed', error);
  });
}

export async function applyVoiceAudioDevices(settings = currentAudioSettings(), { includeMic = true } = {}) {
  const micDeviceId = settings.micDeviceId || '';
  const speakerDeviceId = settings.speakerDeviceId || '';
  const device = voiceDeviceInstance;

  if (device?.audio) {
    if (includeMic) {
      try {
        if (micDeviceId) {
          await device.audio.setInputDevice(micDeviceId);
        } else if (device.audio.unsetInputDevice) {
          await device.audio.unsetInputDevice();
        }
      } catch (error) {
        console.warn('[VOICE] setInputDevice failed', error);
      }
    }
    try {
      if (speakerDeviceId && device.audio.speakerDevices?.set) {
        await device.audio.speakerDevices.set(speakerDeviceId);
      }
    } catch (error) {
      console.warn('[VOICE] speakerDevices.set failed', error);
    }
  }

  await applySpeakerToElement(remoteAudio, speakerDeviceId);
  if (activeCallRecord?.call) attachRemoteStream(activeCallRecord.call);
}

function getCallEventDetail(call) {
  const params = call?.parameters || {};
  return {
    from: params.From || params.from || '',
    to: params.To || params.to || '',
    callSid: params.CallSid || params.call_sid || params.callSid || '',
  };
}

function extractCallSid(call) {
  try {
    const params = call?.parameters || {};
    return String(params.CallSid || params.call_sid || params.callSid || '').trim();
  } catch (_) {
    return '';
  }
}

function getTwilioCallStatus(call) {
  if (!call) return '';
  try {
    const status = typeof call.status === 'function' ? call.status() : call.status;
    return String(status || '').toLowerCase();
  } catch (_) {
    return '';
  }
}

function formatParty(value) {
  const text = String(value || '').trim();
  return text || 'Unknown';
}

function isBusyState(state) {
  return BUSY_STATES.has(state);
}

function recordMatchesCall(record, call) {
  if (!record || !call) return false;
  if (record.call === call) return true;
  const sid = extractCallSid(call);
  if (sid && record.callSid && sid === record.callSid) return true;
  return false;
}

function clearRemoteAudio() {
  if (!remoteAudio) return;
  try {
    remoteAudio.pause?.();
  } catch (_) {}
  try {
    remoteAudio.srcObject = null;
  } catch (_) {}
}

function clearIncomingCallNotification(notificationId) {
  if (!notificationId) return;
  try {
    notificationStore.markAsRead(notificationId);
    notificationStore.dismissModal(notificationId);
  } catch (_) {}
}

async function notifyIncomingCall(record) {
  const info = getCallEventDetail(record.call);
  const notificationId = record.callSid
    ? `voice-incoming-${record.callSid}`
    : `voice-incoming-${record.internalId}`;

  record.notificationId = notificationId;

  const event = {
    id: notificationId,
    severity: 'warning',
    source: 'voice',
    type: 'incoming_call',
    timestamp: new Date().toISOString(),
    alertMeta: { flash: true, callState: 'ringing' },
    context: {
      route: '/home',
      status: 'ringing',
      meta: {
        title: 'Incoming Call',
        message: `From: ${formatParty(info.from)} • To: ${formatParty(info.to)}`,
        callSid: record.callSid || null,
        internalCallId: record.internalId,
      },
    },
  };

  try {
    await emitAppNotification(event, { skipOSNotification: false });
  } catch (error) {
    console.warn('[VOICE] Failed to emit incoming call notification', error);
  }
}

function correlationDetail(record, extra = {}) {
  return {
    callSid: record?.callSid || null,
    internalCallId: record?.internalId || null,
    endpointSessionId: ensureEndpointSessionId(),
    state: record?.state || VOICE_CALL_STATE.IDLE,
    ...extra,
  };
}

function resolveOperatorEndReason(record, sdkReason) {
  if (record?.localEndReason) return record.localEndReason;
  if (sdkReason === 'reject') return VOICE_END_REASON.REJECTED;
  if (sdkReason === 'error' || sdkReason === 'transportClose') {
    return VOICE_END_REASON.CONNECTION_FAILED;
  }
  if (sdkReason === 'cancel') {
    // Twilio cancel covers caller hangup and answered-elsewhere; do not invent which.
    return VOICE_END_REASON.MISSED;
  }
  if (sdkReason === 'disconnect') return VOICE_END_REASON.ENDED;
  if (sdkReason === 'hangup' || sdkReason === 'device_destroy') {
    return record?.state === VOICE_CALL_STATE.INCOMING || record?.state === VOICE_CALL_STATE.ACCEPTING
      ? VOICE_END_REASON.REJECTED
      : VOICE_END_REASON.ENDED;
  }
  return VOICE_END_REASON.ENDED;
}

/**
 * Clear UI/module state only when the event belongs to the current record.
 * Idempotent: safe if already cleared or mismatched.
 */
function endCallRecordLocally(record, reason = 'ended') {
  if (!record) return false;
  if (activeCallRecord && activeCallRecord !== record) {
    voiceLog('stale_cleanup_ignored', {
      reason,
      staleInternalCallId: record.internalId,
      staleCallSid: record.callSid || null,
      activeInternalCallId: activeCallRecord.internalId,
      activeCallSid: activeCallRecord.callSid || null,
    });
    return false;
  }
  if (!activeCallRecord) {
    clearIncomingCallNotification(record.notificationId);
    return true;
  }

  const previousState = activeCallRecord.state;
  const endReason = resolveOperatorEndReason(record, reason);
  clearRemoteAudio();
  clearIncomingCallNotification(activeCallRecord.notificationId);
  stopIncomingRingtone(reason);
  activeCallRecord.state = VOICE_CALL_STATE.ENDED;
  activeCallRecord = null;
  outboundInFlight = false;
  isMuted = false;
  voiceLog('call_ended', {
    reason,
    endReason,
    previousState,
    newState: VOICE_CALL_STATE.IDLE,
    internalCallId: record.internalId,
    callSid: record.callSid || null,
  });
  window.dispatchEvent(new CustomEvent('voice-call-ended', {
    detail: correlationDetail(record, {
      reason,
      endReason,
      previousState,
    }),
  }));
  flushDeferredRegisterAfterIdle();
  return true;
}

function stopIncomingRingtone(reason = 'stop') {
  try {
    const cache = voiceDeviceInstance?._soundcache;
    const incoming = cache?.get?.('incoming') || cache?.get?.(Device.SoundName?.Incoming);
    if (incoming && typeof incoming.stop === 'function') {
      incoming.stop();
      voiceLog('incoming_ringtone_stopped', { reason });
    }
  } catch (error) {
    console.warn('[VOICE] stopIncomingRingtone failed', error);
  }
}

function rejectCompetingInvite(call, reason) {
  const callSid = extractCallSid(call);
  voiceLog('competing_invite_rejected', {
    reason,
    competingCallSid: callSid || null,
    activeInternalCallId: activeCallRecord?.internalId || null,
    activeCallSid: activeCallRecord?.callSid || null,
    activeState: activeCallRecord?.state || null,
  });
  try {
    if (typeof call.reject === 'function') {
      call.reject();
    } else if (typeof call.ignore === 'function') {
      call.ignore();
    }
  } catch (error) {
    console.warn('[VOICE] competing invite reject failed', error);
  }
  // Rejected invite may have already started Device ringtone; force stop if our
  // active call is already connected/accepted (SDK may keep sound while _calls nonempty).
  if (
    activeCallRecord
    && (activeCallRecord.state === VOICE_CALL_STATE.ACTIVE
      || activeCallRecord.state === VOICE_CALL_STATE.CONNECTED
      || activeCallRecord.state === VOICE_CALL_STATE.ACCEPTING)
  ) {
    stopIncomingRingtone('competing_rejected_while_active');
  }
}

function bindCallEvents(record) {
  const call = record.call;
  // Do NOT removeAllListeners — Twilio Device attaches once('accept') to stop the
  // incoming ringtone and once(cancel/disconnect/reject) for its own bookkeeping.

  call.on('accept', () => {
    if (!recordMatchesCall(activeCallRecord, call)) return;
    const sid = extractCallSid(call);
    if (sid) record.callSid = sid;
    const previousState = record.state;
    record.state = record.outboundParams
      ? VOICE_CALL_STATE.CONNECTED
      : VOICE_CALL_STATE.ACTIVE;
    stopIncomingRingtone('call_accept_event');
    clearIncomingCallNotification(record.notificationId);
    record.notificationId = null;

    const cp = call.customParameters;
    const callType = cp?.get?.('callType') || cp?.callType;
    const sessionId = cp?.get?.('sessionId') || cp?.sessionId;
    const tenantId = cp?.get?.('tenantId') || cp?.tenantId;
    if (callType === 'contact_list_call' && sessionId && tenantId) {
      const db = getFirestore();
      const sessionRef = doc(db, 'tenants', tenantId, 'contactListCallSessions', sessionId);
      updateDoc(sessionRef, {
        status: 'active',
        answeredAt: new Date().toISOString(),
      }).catch((e) => console.warn('[VOICE] Failed to update session status:', e));
    }

    attachRemoteStream(call);
    if (record.outboundParams) {
      isMuted = false;
      try {
        call.mute(false);
      } catch (error) {
        console.warn('[VOICE] unmute outbound failed', error);
      }
      // Do not call setInputDevice again here. Replacing the mic as the SIP
      // leg answers drops the outbound track while remote audio still plays.
      applyVoiceAudioDevices(currentAudioSettings(), { includeMic: false }).catch((error) => {
        console.warn('[VOICE] apply speaker after outbound accept failed', error);
      });
    } else {
      applyVoiceAudioDevices().catch((error) => {
        console.warn('[VOICE] apply audio devices after accept failed', error);
      });
    }

    voiceLog('call_accepted', {
      previousState,
      newState: record.state,
      callSid: record.callSid || null,
      internalCallId: record.internalId,
    });
    window.dispatchEvent(new CustomEvent('voice-call-active', {
      detail: {
        ...(record.outboundParams || getCallEventDetail(call)),
        ...correlationDetail(record),
      },
    }));
  });

  const onTerminal = (sdkReason) => {
    if (!recordMatchesCall(activeCallRecord, call) && activeCallRecord !== record) {
      voiceLog('stale_terminal_ignored', {
        reason: sdkReason,
        eventCallSid: extractCallSid(call) || null,
        eventInternalCallId: record.internalId,
      });
      return;
    }
    stopIncomingRingtone(sdkReason);
    endCallRecordLocally(record, sdkReason);
  };

  call.on('disconnect', () => onTerminal('disconnect'));
  call.on('cancel', () => onTerminal('cancel'));
  call.on('reject', () => onTerminal('reject'));
  call.on('error', (error) => {
    console.error('[VOICE] call.error', error);
    if (!recordMatchesCall(activeCallRecord, call) && activeCallRecord !== record) {
      voiceLog('stale_error_ignored', {
        eventCallSid: extractCallSid(call) || null,
        eventInternalCallId: record.internalId,
      });
      return;
    }
    window.dispatchEvent(new CustomEvent('voice-outbound-error', {
      detail: {
        error: error?.message || String(error),
        ...correlationDetail(record, { endReason: VOICE_END_REASON.CONNECTION_FAILED }),
      },
    }));
    stopIncomingRingtone('error');
    endCallRecordLocally(record, 'error');
  });

  // Pending invites can die on signaling drop without cancel/disconnect.
  if (typeof call.on === 'function') {
    call.on('transportClose', () => {
      if (!recordMatchesCall(activeCallRecord, call) && activeCallRecord !== record) {
        voiceLog('stale_transportClose_ignored', {
          eventCallSid: extractCallSid(call) || null,
          eventInternalCallId: record.internalId,
        });
        return;
      }
      const status = getTwilioCallStatus(call);
      if (status && status !== 'pending' && status !== 'ringing' && status !== 'closed') {
        return;
      }
      if (
        record.state !== VOICE_CALL_STATE.INCOMING
        && record.state !== VOICE_CALL_STATE.ACCEPTING
        && record.state !== VOICE_CALL_STATE.OUTBOUND_RINGING
      ) {
        return;
      }
      stopIncomingRingtone('transportClose');
      endCallRecordLocally(record, 'transportClose');
    });
  }
}

async function fetchVoiceToken() {
  // Server derives identity from authenticated UID; do not send client identity.
  const response = await authenticatedFetch('/api/twilio/voice-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Voice token request failed: ${response.status} ${text}`);
  }

  const data = await response.json();
  if (!data?.token) throw new Error('Voice token response missing token');
  return data.token;
}

function isDeviceRegistered(device = voiceDeviceInstance) {
  if (!device) return false;
  const state = String(device.state || '').toLowerCase();
  return state === 'registered' || state === Device.State?.Registered;
}

function flushDeferredRegisterAfterIdle() {
  if (!deferredRegisterAfterIdle) return;
  if (activeCallRecord && isBusyState(activeCallRecord.state)) return;
  deferredRegisterAfterIdle = false;
  refreshVoiceToken('deferred_idle_register').catch((error) => {
    console.error('[VOICE] deferred register after idle failed', error);
    voiceDeviceReady = false;
  });
}

/**
 * Twilio Voice JS recommends updateToken() on tokenWillExpire.
 * Official docs/examples do NOT require register() after updateToken when already
 * registered; register() is only needed when the Device is not registered.
 * We never call register() during a pending/active call (avoids invite disruption).
 */
async function refreshVoiceToken(reason = 'unknown') {
  if (!voiceDeviceInstance || !voiceDeviceUid) return;
  if (tokenRefreshPromise) return tokenRefreshPromise;

  const busy = Boolean(activeCallRecord && isBusyState(activeCallRecord.state));

  tokenRefreshPromise = (async () => {
    try {
      const nextToken = await fetchVoiceToken();
      voiceDeviceInstance.updateToken(nextToken);
      voiceLog('token_updated', { reason, busy, registered: isDeviceRegistered() });

      if (busy) {
        if (!isDeviceRegistered()) {
          deferredRegisterAfterIdle = true;
          voiceLog('register_deferred_until_idle', { reason });
        }
        return;
      }

      // Idle: register only when not already registered (or flushing a deferral).
      if (!isDeviceRegistered() || reason === 'deferred_idle_register') {
        try {
          await voiceDeviceInstance.register();
          voiceDeviceReady = true;
          voiceLog('device_registered_after_token', { reason });
        } catch (registerError) {
          voiceDeviceReady = false;
          console.warn('[VOICE] register after token refresh failed', { reason, registerError });
          voiceLog('register_failed', {
            reason,
            error: registerError?.message || String(registerError),
          });
        }
      } else {
        voiceDeviceReady = true;
      }
    } catch (error) {
      voiceDeviceReady = false;
      console.error('[VOICE] token refresh failed', { reason, error });
      voiceLog('token_refresh_failed', {
        reason,
        error: error?.message || String(error),
      });
      throw error;
    }
  })();

  try {
    await tokenRefreshPromise;
  } finally {
    tokenRefreshPromise = null;
  }
}

function attachDeviceListeners(device) {
  device.removeAllListeners?.('registered');
  device.removeAllListeners?.('tokenWillExpire');
  device.removeAllListeners?.('error');
  device.removeAllListeners?.('incoming');

  device.on('registered', () => {
    voiceDeviceReady = true;
    voiceLog('device_registered');
  });

  device.on('tokenWillExpire', () => {
    refreshVoiceToken('tokenWillExpire').catch((error) => {
      voiceDeviceReady = false;
      console.error('[VOICE] tokenWillExpire refresh failed', error);
    });
  });

  device.on('error', (error) => {
    console.error('[VOICE] device.error', error);
    const code = Number(error?.code);
    const message = String(error?.message || '');
    if (code === 20104 || /accesstokenexpired/i.test(message)) {
      refreshVoiceToken('deviceErrorAccessTokenExpired').catch((refreshError) => {
        voiceDeviceReady = false;
        console.error('[VOICE] AccessTokenExpired refresh failed', refreshError);
      });
    }
  });

  device.on('incoming', (call) => {
    const callSid = extractCallSid(call);
    if (activeCallRecord && isBusyState(activeCallRecord.state)) {
      rejectCompetingInvite(call, `busy_${activeCallRecord.state}`);
      return;
    }

    const record = {
      call,
      internalId: nextInternalCallId(),
      callSid: callSid || '',
      state: VOICE_CALL_STATE.INCOMING,
      detail: getCallEventDetail(call),
      notificationId: null,
      outboundParams: null,
      localEndReason: null,
    };
    record.detail = {
      ...record.detail,
      ...correlationDetail(record),
    };
    activeCallRecord = record;
    voiceLog('incoming', {
      previousState: VOICE_CALL_STATE.IDLE,
      newState: VOICE_CALL_STATE.INCOMING,
      callSid: record.callSid || null,
      internalCallId: record.internalId,
    });
    notifyIncomingCall(record);
    window.dispatchEvent(new CustomEvent('voice-incoming-call', {
      detail: record.detail,
    }));
    bindCallEvents(record);
  });
}

export async function initVoiceDevice(uid) {
  if (!uid) {
    console.warn('[VOICE] initVoiceDevice skipped: missing uid');
    return null;
  }

  ensureEndpointSessionId();

  if (voiceDeviceInstance && voiceDeviceUid === uid) {
    return voiceDeviceInstance;
  }

  if (voiceDeviceInitPromise) {
    return voiceDeviceInitPromise;
  }

  voiceDeviceInitPromise = (async () => {
    if (voiceDeviceInstance && voiceDeviceUid !== uid) {
      await destroyVoiceDevice();
    }

    const token = await fetchVoiceToken();

    const device = new Device(token, {
      codecPreferences: ['opus', 'pcmu'],
      debug: false,
      edge,
    });

    attachDeviceListeners(device);
    voiceDeviceReady = false;
    try {
      await device.register();
      voiceDeviceReady = true;
    } catch (registerError) {
      // Outbound Device.connect() can still work if incoming registration fails.
      voiceDeviceReady = false;
      console.warn('[VOICE] device.register failed; keeping device for outbound calls', registerError);
    }

    voiceDeviceInstance = device;
    voiceDeviceUid = uid;
    voiceLog('device_initialized', { uid, ready: voiceDeviceReady });
    await applyVoiceAudioDevices();
    return device;
  })();

  try {
    return await voiceDeviceInitPromise;
  } finally {
    voiceDeviceInitPromise = null;
  }
}

/**
 * Tear down the active Twilio Call correctly:
 * - unanswered inbound → reject() (disconnect leaves the invite ringing)
 * - connected / outbound → disconnect()
 */
function terminateActiveCall(reason = 'hangup') {
  const record = activeCallRecord;
  if (!record?.call) {
    if (record) endCallRecordLocally(record, reason);
    else {
      clearRemoteAudio();
      outboundInFlight = false;
      isMuted = false;
    }
    return;
  }

  const previousState = record.state;
  record.state = VOICE_CALL_STATE.TERMINATING;
  const status = getTwilioCallStatus(record.call);
  const isPendingInbound =
    previousState === VOICE_CALL_STATE.INCOMING
    || previousState === VOICE_CALL_STATE.ACCEPTING
    || status === 'pending'
    || status === 'ringing';

  if (isPendingInbound) {
    record.localEndReason = VOICE_END_REASON.REJECTED;
  } else if (reason === 'hangup' || reason === 'device_destroy') {
    record.localEndReason = VOICE_END_REASON.ENDED;
  }

  voiceLog('terminate', {
    reason,
    previousState,
    isPendingInbound,
    callSid: record.callSid || null,
    internalCallId: record.internalId,
  });

  try {
    if (isPendingInbound && typeof record.call.reject === 'function') {
      record.call.reject();
    } else if (typeof record.call.disconnect === 'function') {
      record.call.disconnect();
    } else if (typeof record.call.reject === 'function') {
      record.call.reject();
    }
  } catch (error) {
    console.warn('[VOICE] terminateActiveCall failed', error);
    try { record.call.disconnect?.(); } catch (_) {}
    try { record.call.reject?.(); } catch (_) {}
  }

  stopIncomingRingtone(reason);
  // Always clear local UI for THIS record. Twilio cancel/disconnect also call end;
  // call-scoped guards make those idempotent.
  endCallRecordLocally(record, reason);
}

export async function destroyVoiceDevice() {
  terminateActiveCall('device_destroy');
  if (voiceDeviceInstance) {
    try { voiceDeviceInstance.destroy(); } catch (_) {}
  }
  voiceDeviceInstance = null;
  voiceDeviceUid = null;
  tokenRefreshPromise = null;
  deferredRegisterAfterIdle = false;
  voiceDeviceReady = false;
  voiceLog('device_destroyed');
}

export function isVoiceDeviceReady() {
  return Boolean(voiceDeviceInstance) && voiceDeviceReady;
}

export function isCallInProgress() {
  return Boolean(activeCallRecord && isBusyState(activeCallRecord.state)) || outboundInFlight;
}

export function getActiveCallSnapshot() {
  if (!activeCallRecord) {
    return { state: null, detail: null, callSid: null, internalCallId: null };
  }
  return {
    state: activeCallRecord.state,
    detail: activeCallRecord.detail,
    callSid: activeCallRecord.callSid || null,
    internalCallId: activeCallRecord.internalId,
  };
}

export function getVoiceEndpointSessionId() {
  return ensureEndpointSessionId();
}

export function acceptCall() {
  const record = activeCallRecord;
  if (!record?.call || record.state !== VOICE_CALL_STATE.INCOMING) {
    console.warn('[VOICE] acceptCall ignored; no pending incoming call');
    return;
  }

  const status = getTwilioCallStatus(record.call);
  if (status && status !== 'pending' && status !== 'ringing') {
    console.warn('[VOICE] acceptCall ignored; call status is', status);
    endCallRecordLocally(record, 'accept_invalid_status');
    return;
  }

  record.state = VOICE_CALL_STATE.ACCEPTING;
  voiceLog('accept_attempt', {
    callSid: record.callSid || null,
    internalCallId: record.internalId,
  });

  try {
    record.call.accept();
    record.state = VOICE_CALL_STATE.ACTIVE;
    // Belt-and-suspenders: never leave Device ringtone playing after Answer.
    stopIncomingRingtone('acceptCall');
    attachRemoteStream(record.call);
    applyVoiceAudioDevices().catch((error) => {
      console.warn('[VOICE] apply audio devices after acceptCall failed', error);
    });
    clearIncomingCallNotification(record.notificationId);
    record.notificationId = null;
    window.dispatchEvent(new CustomEvent('voice-call-active', {
      detail: {
        ...getCallEventDetail(record.call),
        ...correlationDetail(record),
      },
    }));
  } catch (error) {
    console.error('[VOICE] acceptCall failed', error);
    terminateActiveCall('accept_failed');
  }
}

export function hangupCall() {
  terminateActiveCall('hangup');
}

export async function startOutboundCall(params) {
  if (!voiceDeviceInstance) {
    throw new Error('Voice device not initialized. Please reload the page and try again.');
  }
  if (outboundInFlight || (activeCallRecord && isBusyState(activeCallRecord.state))) {
    throw new Error('A call is already in progress.');
  }

  outboundInFlight = true;
  try {
    const settings = currentAudioSettings();
    await applyVoiceAudioDevices(settings);
    const connectOpts = { params };
    const call = await voiceDeviceInstance.connect(connectOpts);
    const record = {
      call,
      internalId: nextInternalCallId(),
      callSid: extractCallSid(call) || '',
      state: VOICE_CALL_STATE.OUTBOUND_RINGING,
      detail: params,
      notificationId: null,
      outboundParams: params,
    };
    activeCallRecord = record;
    bindCallEvents(record);
    attachRemoteStream(call);
    voiceLog('outbound_started', {
      callSid: record.callSid || null,
      internalCallId: record.internalId,
    });
    return call;
  } catch (error) {
    outboundInFlight = false;
    throw error instanceof Error ? error : new Error(error?.message || 'Unable to start the call');
  }
}

export async function startPstnCall({ to, displayName = '' }) {
  return startOutboundCall({
    action: 'browser_pstn_call',
    to,
    displayName,
  });
}

export async function startSipCall({ targetId, sipUri } = {}) {
  const uri = String(sipUri || '').trim();
  if (uri) {
    const normalized = /^sips?:/i.test(uri) ? uri : (uri.includes('@') ? `sip:${uri}` : '');
    if (!normalized || /[\s;<>"']/.test(normalized) || /^sips?:[^@]*:/i.test(normalized)) {
      throw new Error('Enter a SIP address like sip:user@domain. Passwords in the address are not accepted.');
    }
    return startOutboundCall({
      action: 'browser_sip_call',
      sipUri: normalized,
    });
  }
  const id = String(targetId || '').trim();
  if (!id || /^sips?:/i.test(id) || id.includes('@')) {
    throw new Error('Select a SIP device from your tenant.');
  }
  return startOutboundCall({
    action: 'browser_sip_call',
    targetId: id,
  });
}

export function sendDtmf(digit) {
  if (!activeCallRecord?.call || !digit) return;
  try {
    activeCallRecord.call.sendDigits(String(digit));
  } catch (error) {
    console.warn('[VOICE] sendDtmf failed', error);
  }
}

export function toggleMute() {
  if (!activeCallRecord?.call) return isMuted;
  isMuted = !isMuted;
  try {
    activeCallRecord.call.mute(isMuted);
  } catch (error) {
    console.warn('[VOICE] mute failed', error);
  }
  return isMuted;
}

/** Test-only: inject a Device-like object (e.g. soundcache) without full init. */
export function __injectVoiceDeviceForTests(device) {
  voiceDeviceInstance = device;
}

/** Test-only: reset module singletons between vitest cases. */
export function __resetVoiceCallServiceForTests() {
  activeCallRecord = null;
  voiceDeviceInstance = null;
  voiceDeviceUid = null;
  voiceDeviceInitPromise = null;
  tokenRefreshPromise = null;
  deferredRegisterAfterIdle = false;
  voiceDeviceReady = false;
  outboundInFlight = false;
  isMuted = false;
  endpointSessionId = null;
  internalCallSeq = 0;
  clearRemoteAudio();
}

/** Test-only: expose token refresh path. */
export async function __refreshVoiceTokenForTests(reason = 'test') {
  return refreshVoiceToken(reason);
}

/** Test-only: inspect deferred-register flag. */
export function __getDeferredRegisterForTests() {
  return deferredRegisterAfterIdle;
}

/** Test-only: set ready flag after mock register. */
export function __setVoiceDeviceReadyForTests(ready) {
  voiceDeviceReady = Boolean(ready);
}

/** Test-only: set authenticated uid for token refresh. */
export function __setVoiceDeviceUidForTests(uid) {
  voiceDeviceUid = uid || null;
}

/** Test-only: inject a pending inbound call without a live Device. */
export function __setActiveCallRecordForTests(record) {
  activeCallRecord = record;
}

/** Test-only: expose terminate for direct assertions. */
export function __terminateActiveCallForTests(reason = 'hangup') {
  terminateActiveCall(reason);
}

/** Test-only: simulate device incoming handler. */
export function __handleIncomingForTests(call) {
  if (!voiceDeviceInstance) {
    voiceDeviceInstance = { _test: true };
  }
  const listeners = voiceDeviceInstance._incomingHandler;
  if (typeof listeners === 'function') {
    listeners(call);
    return;
  }
  // Fall through to same logic as attachDeviceListeners incoming
  const callSid = extractCallSid(call);
  if (activeCallRecord && isBusyState(activeCallRecord.state)) {
    rejectCompetingInvite(call, `busy_${activeCallRecord.state}`);
    return;
  }
  const record = {
    call,
    internalId: nextInternalCallId(),
    callSid: callSid || '',
    state: VOICE_CALL_STATE.INCOMING,
    detail: getCallEventDetail(call),
    notificationId: null,
    outboundParams: null,
    localEndReason: null,
  };
  record.detail = {
    ...record.detail,
    ...correlationDetail(record),
  };
  activeCallRecord = record;
  notifyIncomingCall(record);
  window.dispatchEvent(new CustomEvent('voice-incoming-call', { detail: record.detail }));
  bindCallEvents(record);
}
